import * as Blockly from 'blockly/core';
import { Order } from './order';

/**
 * C++ / Arduino generator.
 *
 * Contract: every registered block type has exactly one handler here, each
 * handler emits compilable Arduino C++ (Arduino-ESP32 core 3.x), and the
 * assembled sketch is deterministic for a given workspace.
 */

export interface Diagnostic {
  level: 'warning' | 'error';
  message: string;
}

export interface PinUsage {
  pin: number;
  role: string;
  peripheral: string;
}

export interface GenResult {
  cpp: string;
  diagnostics: Diagnostic[];
  pins: PinUsage[];
  libraries: string[];
}

/** Peripheral pairs that may legitimately share a pin. */
const COMPATIBLE_PAIRS = new Set([
  'gpio|interrupt',
  'interrupt|gpio',
  'gpio|pwm',
  'pwm|gpio',
]);

const INCLUDE_PRIORITY: Record<string, number> = {
  'Wire.h': 1,
  'SPI.h': 2,
  'Ethernet.h': 3,
  'WiFi.h': 4,
  'HTTPClient.h': 5,
  'PubSubClient.h': 6,
  'ESP32Servo.h': 7,
  'Adafruit_GFX.h': 8,
  'Adafruit_SSD1306.h': 9,
  'Adafruit_ST7735.h': 9,
  'Adafruit_ILI9341.h': 9,
  'esp_sleep.h': 10,
};

const CPP_RESERVED_WORDS = new Set([
  'alignas', 'alignof', 'and', 'auto', 'bool', 'break', 'case', 'catch', 'char',
  'class', 'const', 'constexpr', 'continue', 'default', 'delete', 'do', 'double',
  'else', 'enum', 'explicit', 'export', 'extern', 'false', 'float', 'for', 'friend',
  'goto', 'if', 'inline', 'int', 'long', 'mutable', 'namespace', 'new', 'noexcept',
  'not', 'nullptr', 'operator', 'or', 'private', 'protected', 'public', 'register',
  'return', 'short', 'signed', 'sizeof', 'static', 'struct', 'switch', 'template',
  'this', 'throw', 'true', 'try', 'typedef', 'typeid', 'typename', 'union',
  'unsigned', 'using', 'virtual', 'void', 'volatile', 'while', 'setup', 'loop',
  'HIGH', 'LOW', 'INPUT', 'OUTPUT', 'INPUT_PULLUP', 'INPUT_PULLDOWN',
]);

const HAT_TYPES = new Set([
  'esp32_on_setup',
  'esp32_on_loop',
  'esp32_on_interrupt',
  'esp32_on_mqtt_message',
  'esp32_on_serial_line',
]);

function sanitizeId(id: string): string {
  return id.replace(/[^A-Za-z0-9]/g, '') || 'x';
}

function indent(code: string, unit = '  '): string {
  return code
    .split('\n')
    .map((line) => (line.length ? unit + line : line))
    .join('\n');
}

function inferCppType(valueCode: string): string {
  const c = valueCode.trim();
  if (c.startsWith('"') || c.startsWith('String(')) return 'String';
  if (/^(true|false)\b/.test(c) || c.includes('&&') || c.includes('||') || c.startsWith('!')) {
    return 'bool';
  }
  if (/^-?\d+\.\d+$/.test(c)) return 'float';
  return 'int';
}

/** Serial line polling helper, emitted once when a serial-line hat exists. */
const SERIAL_POLL_HELPER = `void esp32PollSerialLines() {
  static String esp32SerialBuf;
  while (Serial.available() > 0) {
    char c = (char)Serial.read();
    if (c == '\\r') continue;
    if (c == '\\n') {
      if (esp32SerialBuf.length() > 0) {
        String line = esp32SerialBuf;
        esp32SerialBuf = "";
        esp32OnSerialLine(line);
      }
    } else if (esp32SerialBuf.length() < 256) {
      esp32SerialBuf += c;
    }
  }
}`;

const I2C_READ_HELPER = `uint8_t i2cReadReg8(uint8_t addr, uint8_t reg) {
  Wire.beginTransmission(addr);
  Wire.write(reg);
  Wire.endTransmission(false);
  Wire.requestFrom((int)addr, 1);
  return Wire.available() ? Wire.read() : 0;
}`;

const I2C_SCAN_HELPER = `void i2cScanDevices() {
  Serial.println(F("Scanning I2C bus..."));
  uint8_t found = 0;
  for (uint8_t a = 1; a < 127; a++) {
    Wire.beginTransmission(a);
    if (Wire.endTransmission() == 0) {
      Serial.print(F("Device found at 0x"));
      if (a < 16) Serial.print('0');
      Serial.println(a, HEX);
      found++;
    }
  }
  if (found == 0) Serial.println(F("No I2C devices found."));
}`;

const HTTP_GET_HELPER = `String httpGetBlock(const String& url) {
  HTTPClient http;
  http.begin(url);
  http.setTimeout(10000);
  int code = http.GET();
  String body;
  if (code == 200) {
    body = http.getString();
  } else {
    Serial.print(F("HTTP GET failed, code: "));
    Serial.println(code);
  }
  http.end();
  return body;
}`;

const HTTP_POST_HELPER = `int httpPostJsonBlock(const String& url, const String& jsonBody) {
  HTTPClient http;
  http.begin(url);
  http.setTimeout(10000);
  http.addHeader("Content-Type", "application/json");
  int code = http.POST(jsonBody);
  http.end();
  return code;
}`;

const DISPLAY_COLORS_GLOBAL = `const uint16_t SPB_BLACK = 0x0000;
const uint16_t SPB_WHITE = 0xFFFF;
const uint16_t SPB_RED = 0xF800;
const uint16_t SPB_GREEN = 0x07E0;
const uint16_t SPB_BLUE = 0x001F;
const uint16_t SPB_CYAN = 0x07FF;
const uint16_t SPB_MAGENTA = 0xF81F;
const uint16_t SPB_YELLOW = 0xFFE0;
const uint16_t SPB_ORANGE = 0xFD20;`;

function motorRunStopFunctions(model: string): string {
  if (model === 'DRV8833') {
    return `void espMotorRun(int m, int speed) {
  speed = constrain(speed, -255, 255);
  int s = min(abs(speed), 255);
  if (m == 0 || m == 2) {
    if (speed >= 0) { analogWrite(MTR_IN1, s); analogWrite(MTR_IN2, 0); }
    else            { analogWrite(MTR_IN1, 0); analogWrite(MTR_IN2, s); }
  }
  if (m == 1 || m == 2) {
    if (speed >= 0) { analogWrite(MTR_IN3, s); analogWrite(MTR_IN4, 0); }
    else            { analogWrite(MTR_IN3, 0); analogWrite(MTR_IN4, s); }
  }
}

void espMotorStop(int m) {
  if (m == 0 || m == 2) { analogWrite(MTR_IN1, 0); analogWrite(MTR_IN2, 0); }
  if (m == 1 || m == 2) { analogWrite(MTR_IN3, 0); analogWrite(MTR_IN4, 0); }
}`;
  }
  return `void espMotorRun(int m, int speed) {
  speed = constrain(speed, -255, 255);
  int s = min(abs(speed), 255);
  if (m == 0 || m == 2) {
    digitalWrite(MTR_IN1, speed >= 0 ? HIGH : LOW);
    digitalWrite(MTR_IN2, speed >= 0 ? LOW : HIGH);
    if (MTR_ENA >= 0) analogWrite(MTR_ENA, s);
  }
  if (m == 1 || m == 2) {
    digitalWrite(MTR_IN3, speed >= 0 ? HIGH : LOW);
    digitalWrite(MTR_IN4, speed >= 0 ? LOW : HIGH);
    if (MTR_ENB >= 0) analogWrite(MTR_ENB, s);
  }
}

void espMotorStop(int m) {
  if (m == 0 || m == 2) { if (MTR_ENA >= 0) analogWrite(MTR_ENA, 0); }
  if (m == 1 || m == 2) { if (MTR_ENB >= 0) analogWrite(MTR_ENB, 0); }
}`;
}

class SketchPlan {
  includes = new Set<string>();
  libraries = new Set<string>();
  globals: string[] = [];
  helpers = new Map<string, string>();
  setupOnce = new Map<string, string>();
  functions: string[] = [];
  userSetup = '';
  userLoop = '';
  pinUses = new Map<number, PinUsage>();
  diagnostics: Diagnostic[] = [];
  declaredGlobals = new Set<string>();
  declaredVars = new Map<string, string>();
  warnedUndeclared = new Set<string>();
  mqttDispatch: string[] = [];
  mqttHandlerCount = 0;
  usesSerial = false;
  usesSerialLines = false;
  usesWifi = false;
  usesWire = false;
  usesMqtt = false;
  usesServo = false;
  usesMotor = false;
  motorConfigured = false;
  usesDisplay = false;
  displayKind: 'none' | 'oled' | 'tft' = 'none';
  usesEth = false;
  servoPins = new Set<string>();
  inMqttHandler = false;

  include(header: string): void {
    this.includes.add(header);
  }
  library(name: string): void {
    this.libraries.add(name);
  }
  helper(key: string, code: string): void {
    if (!this.helpers.has(key)) this.helpers.set(key, code);
  }
  setupLine(key: string, line: string, override = false): void {
    if (override || !this.setupOnce.has(key)) this.setupOnce.set(key, line);
  }
  warn(message: string): void {
    this.diagnostics.push({ level: 'warning', message });
  }
  error(message: string): void {
    this.diagnostics.push({ level: 'error', message });
  }
  usePin(pin: number, role: string, peripheral: string): void {
    const existing = this.pinUses.get(pin);
    if (!existing) {
      this.pinUses.set(pin, { pin, role, peripheral });
      return;
    }
    const pair = `${existing.peripheral}|${peripheral}`;
    if (existing.peripheral !== peripheral && !COMPATIBLE_PAIRS.has(pair)) {
      this.error(
        `Pin ${pin} is used for both ${existing.peripheral} and ${peripheral}. Reassign one of them.`,
      );
    }
  }
  useGlobal(name: string): boolean {
    if (this.declaredGlobals.has(name)) return true;
    this.error(`Global "${name}" is used but never declared. Add a "declare global" block first.`);
    return false;
  }
}

export class CppGenerator extends Blockly.CodeGenerator {
  plan = new SketchPlan();

  constructor() {
    super('Cpp');
    this.INDENT = '  ';
    this.RESERVED_WORDS_ = [...CPP_RESERVED_WORDS].join(',');
    this.registerHandlers();
  }

  /* ---------------- public API ---------------- */

  reset(): void {
    this.plan = new SketchPlan();
  }

  /**
   * v11's base init() only resets definition maps — the name database (used
   * for variable collision handling) is created by each language generator.
   */
  init(workspace: Blockly.Workspace): void {
    super.init(workspace);
    this.nameDB_ = new Blockly.Names(this.RESERVED_WORDS_);
    this.nameDB_.reset();
    this.nameDB_.setVariableMap(workspace.getVariableMap());
    this.nameDB_.populateVariables(workspace);
    this.nameDB_.populateProcedures(workspace);
  }

  /** Walk the workspace and produce the sketch. Deterministic output. */
  generate(workspace: Blockly.Workspace): GenResult {
    this.reset();
    this.init(workspace);
    let hatCount = 0;
    for (const block of workspace.getTopBlocks(true)) {
      if (HAT_TYPES.has(block.type)) {
        hatCount++;
        this.blockToCode(block);
      } else {
        this.plan.warn(
          `A "${block.type}" block is not attached to an event block and was ignored.`,
        );
      }
    }
    if (hatCount === 0) {
      this.plan.warn(
        'The program has no event blocks. Add a "when board starts" or "forever, every cycle" block and attach statements to it.',
      );
    }
    const cpp = this.assemble();
    return {
      cpp,
      diagnostics: this.plan.diagnostics,
      pins: [...this.plan.pinUses.values()].sort((a, b) => a.pin - b.pin),
      libraries: [...this.plan.libraries].sort(),
    };
  }

  /* ---------------- assembly ---------------- */

  private assemble(): string {
    const plan = this.plan;

    if (plan.usesSerial && !plan.setupOnce.has('serialbegin')) {
      plan.setupLine('serialbegin', 'Serial.begin(115200);');
      plan.warn('Serial output used without a "serial begin" block; defaulting to 115200 baud.');
    }
    if (plan.usesWire && !plan.setupOnce.has('i2cbegin')) {
      plan.setupLine('i2cbegin', 'Wire.begin(21, 22, 100000);');
      plan.warn('I2C used without an "I2C begin" block; defaulting to SDA 21 / SCL 22 at 100 kHz.');
    }
    if (plan.usesWifi) plan.include('WiFi.h');
    if (plan.usesMqtt) {
      plan.include('WiFi.h');
      plan.include('PubSubClient.h');
      plan.library('PubSubClient');
    }
    if (plan.usesEth) {
      plan.include('Ethernet.h');
      plan.include('SPI.h');
      plan.library('Ethernet');
    }
    if (plan.usesServo) {
      plan.include('ESP32Servo.h');
      plan.library('ESP32Servo');
    }
    if (plan.usesMotor && !plan.motorConfigured) {
      plan.error('A motor block is used but no "motor driver" setup block exists. Add one in "when board starts".');
    }
    if (plan.usesDisplay) {
      plan.include('Adafruit_GFX.h');
      plan.library('Adafruit GFX Library');
      if (plan.displayKind === 'none') {
        plan.error('Display blocks are used but no "OLED display" or "TFT display" setup block exists. Add one in "when board starts".');
      }
      plan.globals.push(DISPLAY_COLORS_GLOBAL);
    }
    if (plan.usesSerialLines) {
      plan.helper('serialpoll', SERIAL_POLL_HELPER);
    }

    if (plan.mqttHandlerCount > 0) {
      const dispatch = plan.mqttDispatch.join('\n  ');
      plan.helper(
        'mqttcallback',
        `void espMqttCallback(char* topic, byte* payload, unsigned int length) {
  String payloadStr;
  payloadStr.reserve(length);
  for (unsigned int i = 0; i < length; i++) {
    payloadStr += (char)payload[i];
  }
  ${dispatch}
}`,
      );
      plan.setupLine('mqttcallback', 'espMqtt.setCallback(espMqttCallback);');
    }

    const parts: string[] = [];
    parts.push('// Generated by SPblock. Edit blocks, not this file.');

    const includes = [...plan.includes].sort((a, b) => {
      const pa = INCLUDE_PRIORITY[a] ?? 90;
      const pb = INCLUDE_PRIORITY[b] ?? 90;
      return pa - pb || a.localeCompare(b);
    });
    for (const inc of includes) parts.push(`#include <${inc}>`);

    if (plan.globals.length) parts.push(plan.globals.join('\n'));
    for (const helper of plan.helpers.values()) parts.push(helper);
    for (const fn of plan.functions) parts.push(fn);

    const setupLines: string[] = [...plan.setupOnce.values()];
    const setupBody = setupLines.join('\n') + (plan.userSetup ? '\n' + plan.userSetup : '');
    parts.push(`void setup() {\n${indent(setupBody.trimEnd()) || '  '}\n}`);

    const loopPrefix: string[] = [];
    if (plan.usesMqtt) loopPrefix.push('espMqtt.loop();');
    if (plan.usesSerialLines) loopPrefix.push('esp32PollSerialLines();');
    const loopBody = [...loopPrefix, plan.userLoop].filter(Boolean).join('\n');
    parts.push(`void loop() {\n${indent(loopBody.trimEnd()) || '  '}\n}`);

    return parts.join('\n\n') + '\n';
  }

  /* ---------------- shared helpers ---------------- */

  /**
   * v11's default scrub_ is a no-op stub; language generators implement
   * next-block chaining themselves. Emit each following statement in order.
   */
  scrub_(block: Blockly.Block, code: string, thisOnly = false): string {
    if (thisOnly) return code;
    let out = code;
    let next = block.getNextBlock();
    while (next) {
      const nextCode = this.blockToCode(next, true);
      out += Array.isArray(nextCode) ? (nextCode[0] ?? '') : (nextCode ?? '');
      next = next.getNextBlock();
    }
    return out;
  }

  private stmtSeq(block: Blockly.Block, inputName: string): string {
    const target = block.getInputTargetBlock(inputName);
    if (!target) return '';
    return (this.blockToCode(target) as string) ?? '';
  }

  private val(
    block: Blockly.Block,
    name: string,
    order: number,
    fallback = '',
  ): string {
    const code = this.valueToCode(block, name, order);
    return code === '' || code === null || code === undefined ? fallback : code;
  }

  private numField(block: Blockly.Block, name: string, fallback: number): string {
    const raw = block.getFieldValue(name);
    const n = Number(raw);
    return Number.isFinite(n) ? String(n) : String(fallback);
  }

  /** Quote a JS/TS string as a C++ string literal. */
  cppString(s: string): string {
    const escaped = s
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/\r/g, '\\r')
      .replace(/\n/g, '\\n')
      .replace(/\t/g, '\\t');
    return `"${escaped}"`;
  }

  /** Variable reference: model id + user-visible name, or null if unbound. */
  private varInfo(block: Blockly.Block, fieldName: string): { id: string; user: string } | null {
    const field = block.getField(fieldName) as Blockly.FieldVariable | null;
    const variable = field?.getVariable();
    if (!variable) return null;
    return { id: variable.getId(), user: variable.name };
  }

  /* ---------------- handlers ---------------- */

  private registerHandlers(): void {
    const P = () => this.plan;

    /* ---- event hats ---- */

    this.forBlock['esp32_on_setup'] = (block) => {
      P().userSetup += this.stmtSeq(block, 'DO');
      return '';
    };

    this.forBlock['esp32_on_loop'] = (block) => {
      P().userLoop += this.stmtSeq(block, 'DO');
      return '';
    };

    this.forBlock['esp32_on_interrupt'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const edge = block.getFieldValue('EDGE');
      const body = this.stmtSeq(block, 'DO');
      const fn = `isr_${sanitizeId(block.id)}`;
      if (/\bdelay(Microseconds)?\(/.test(body)) {
        P().warn(
          'delay() appears inside an interrupt handler. ISRs must be short: set a volatile flag and act in the main loop instead.',
        );
      }
      P().functions.push(`void IRAM_ATTR ${fn}() {\n${indent(body.trimEnd())}\n}`);
      const key = `attach_${pin}_${edge}`;
      if (P().setupOnce.has(key)) {
        P().warn(`Pin ${pin} already has a ${edge} interrupt; only the first handler is attached.`);
      } else {
        P().setupLine(key, `attachInterrupt(digitalPinToInterrupt(${pin}), ${fn}, ${edge});`);
      }
      P().usePin(Number(pin), 'interrupt', 'interrupt');
      return '';
    };

    this.forBlock['esp32_on_serial_line'] = (block) => {
      P().usesSerialLines = true;
      P().usesSerial = true;
      const body = this.stmtSeq(block, 'DO');
      P().functions.push(`void esp32OnSerialLine(const String& line) {\n${indent(body.trimEnd())}\n}`);
      return '';
    };

    this.forBlock['esp32_on_mqtt_message'] = (block) => {
      const topic = block.getFieldValue('TOPIC') as string;
      const index = P().mqttHandlerCount++;
      P().usesMqtt = true;
      const body = (() => {
        P().inMqttHandler = true;
        try {
          return this.stmtSeq(block, 'DO');
        } finally {
          P().inMqttHandler = false;
        }
      })();
      P().functions.push(
        `void esp32MqttHandler${index}(const String& payload) {\n${indent(body.trimEnd())}\n}`,
      );
      P().mqttDispatch.push(`if (String(topic) == ${this.cppString(topic)}) { esp32MqttHandler${index}(payloadStr); }`);
      P().setupLine(`mqttsub_${topic}`, `espMqtt.subscribe(${this.cppString(topic)});`);
      return '';
    };

    /* ---- GPIO ---- */

    this.forBlock['esp32_pin_mode'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const mode = block.getFieldValue('MODE');
      P().usePin(Number(pin), mode.toLowerCase(), 'gpio');
      return `pinMode(${pin}, ${mode});\n`;
    };

    this.forBlock['esp32_digital_write'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const level = block.getFieldValue('LEVEL');
      P().usePin(Number(pin), 'output', 'gpio');
      return `digitalWrite(${pin}, ${level});\n`;
    };

    this.forBlock['esp32_pin_toggle'] = (block) => {
      const pin = block.getFieldValue('PIN');
      P().usePin(Number(pin), 'output', 'gpio');
      return `digitalWrite(${pin}, !digitalRead(${pin}));\n`;
    };

    this.forBlock['esp32_digital_read'] = (block) => {
      const pin = block.getFieldValue('PIN');
      P().usePin(Number(pin), 'input', 'gpio');
      return [`(digitalRead(${pin}) == HIGH)`, Order.ATOMIC];
    };

    /* ---- Analog & PWM (LEDC, core 3.x) ---- */

    this.forBlock['esp32_analog_read'] = (block) => {
      const pin = Number(block.getFieldValue('PIN'));
      P().setupLine('analogres', 'analogReadResolution(12);');
      P().usePin(pin, 'adc', 'adc');
      return [`analogRead(${pin})`, Order.ATOMIC];
    };

    const pwmAutoAttach = (pin: string) => {
      P().setupLine(`pwm_${pin}`, `ledcAttach(${pin}, 5000, 12);`);
    };

    this.forBlock['esp32_analog_write'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const duty = this.val(block, 'DUTY', Order.NONE, '0');
      P().usePin(Number(pin), 'pwm', 'pwm');
      pwmAutoAttach(pin);
      return `ledcWrite(${pin}, ${duty});\n`;
    };

    this.forBlock['esp32_pwm_setup'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const freq = this.numField(block, 'FREQ', 5000);
      const res = block.getFieldValue('RES');
      P().usePin(Number(pin), 'pwm', 'pwm');
      P().setupLine(`pwm_${pin}`, `ledcAttach(${pin}, ${freq}, ${res});`, true);
      return '';
    };

    this.forBlock['esp32_pwm_write'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const duty = this.val(block, 'DUTY', Order.NONE, '0');
      P().usePin(Number(pin), 'pwm', 'pwm');
      pwmAutoAttach(pin);
      return `ledcWrite(${pin}, ${duty});\n`;
    };

    this.forBlock['esp32_pwm_release'] = (block) => {
      const pin = block.getFieldValue('PIN');
      return `ledcDetach(${pin});\n`;
    };

    /* ---- Time ---- */

    this.forBlock['esp32_delay_ms'] = (block) => {
      const d = this.val(block, 'DURATION', Order.NONE, '0');
      return `delay(${d});\n`;
    };

    this.forBlock['esp32_delay_us'] = (block) => {
      const d = this.val(block, 'DURATION', Order.NONE, '0');
      return `delayMicroseconds(${d});\n`;
    };

    this.forBlock['esp32_millis'] = () => [this.millisExpr(), Order.ATOMIC];
    this.forBlock['esp32_micros'] = () => [this.microsExpr(), Order.ATOMIC];

    this.forBlock['esp32_every_ms'] = (block) => {
      const interval = this.numField(block, 'INTERVAL', 1000);
      const id = sanitizeId(block.id);
      const body = this.stmtSeq(block, 'DO');
      return `{
  static uint32_t s_last_${id} = 0;
  uint32_t now = millis();
  if (now - s_last_${id} >= ${interval}UL) {
    s_last_${id} = now;
${indent(body.trimEnd())}
  }
}\n`;
    };

    /* ---- Power ---- */

    this.forBlock['esp32_deep_sleep_timer'] = (block) => {
      const seconds = this.val(block, 'SECONDS', Order.NONE, '10');
      P().include('esp_sleep.h');
      return `esp_sleep_enable_timer_wakeup((uint64_t)(${seconds}) * 1000000ULL);\nesp_deep_sleep_start();\n`;
    };

    this.forBlock['esp32_deep_sleep_ext'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const level = block.getFieldValue('LEVEL');
      P().include('esp_sleep.h');
      P().usePin(Number(pin), 'wake', 'wake');
      return `esp_sleep_enable_ext0_wakeup((gpio_num_t)${pin}, ${level});\nesp_deep_sleep_start();\n`;
    };

    this.forBlock['esp32_wakeup_cause'] = () => {
      P().include('esp_sleep.h');
      return [`(int)esp_sleep_get_wakeup_cause()`, Order.ATOMIC];
    };

    /* ---- Comms: I2C ---- */

    this.forBlock['esp32_i2c_begin'] = (block) => {
      const sda = block.getFieldValue('SDA');
      const scl = block.getFieldValue('SCL');
      const freq = this.numField(block, 'FREQ', 100000);
      P().include('Wire.h');
      P().setupLine('i2cbegin', `Wire.begin(${sda}, ${scl}, ${freq});`);
      P().usePin(Number(sda), 'i2c-sda', 'i2c');
      P().usePin(Number(scl), 'i2c-scl', 'i2c');
      return '';
    };

    this.forBlock['esp32_i2c_write_register'] = (block) => {
      const addr = this.val(block, 'ADDR', Order.NONE, '0');
      const reg = this.val(block, 'REG', Order.NONE, '0');
      const value = this.val(block, 'VALUE', Order.NONE, '0');
      P().usesWire = true;
      P().include('Wire.h');
      return `Wire.beginTransmission(${addr});\nWire.write(${reg});\nWire.write(${value});\nWire.endTransmission();\n`;
    };

    this.forBlock['esp32_i2c_read_register'] = (block) => {
      const addr = this.val(block, 'ADDR', Order.NONE, '0');
      const reg = this.val(block, 'REG', Order.NONE, '0');
      P().usesWire = true;
      P().include('Wire.h');
      P().helper('i2cread', I2C_READ_HELPER);
      return [`i2cReadReg8(${addr}, ${reg})`, Order.ATOMIC];
    };

    this.forBlock['esp32_i2c_scan'] = () => {
      P().usesWire = true;
      P().usesSerial = true;
      P().include('Wire.h');
      P().helper('i2cscan', I2C_SCAN_HELPER);
      return 'i2cScanDevices();\n';
    };

    /* ---- Comms: SPI ---- */

    this.forBlock['esp32_spi_begin'] = (block) => {
      const sck = block.getFieldValue('SCK');
      const miso = block.getFieldValue('MISO');
      const mosi = block.getFieldValue('MOSI');
      const cs = block.getFieldValue('CS');
      P().include('SPI.h');
      P().usePin(Number(sck), 'spi-sck', 'spi');
      P().usePin(Number(miso), 'spi-miso', 'spi');
      P().usePin(Number(mosi), 'spi-mosi', 'spi');
      P().usePin(Number(cs), 'spi-cs', 'spi');
      return `SPI.begin(${sck}, ${miso}, ${mosi}, ${cs});\n`;
    };

    this.forBlock['esp32_spi_transfer'] = (block) => {
      const data = this.val(block, 'DATA', Order.NONE, '0');
      P().include('SPI.h');
      return [`SPI.transfer(${data})`, Order.ATOMIC];
    };

    /* ---- Comms: UART ---- */

    this.forBlock['esp32_serial_begin'] = (block) => {
      const baud = block.getFieldValue('BAUD');
      P().usesSerial = true;
      P().setupLine('serialbegin', `Serial.begin(${baud});`);
      return '';
    };

    this.forBlock['esp32_serial_print'] = (block) => {
      const value = this.val(block, 'VALUE', Order.NONE, this.cppString(''));
      const newline = block.getFieldValue('NEWLINE') === 'TRUE' || block.getFieldValue('NEWLINE') === true;
      P().usesSerial = true;
      return `Serial.${newline ? 'println' : 'print'}(${value});\n`;
    };

    this.forBlock['esp32_serial_available'] = () => {
      P().usesSerial = true;
      return ['(Serial.available() > 0)', Order.ATOMIC];
    };

    this.forBlock['esp32_serial_read_line'] = () => {
      P().usesSerial = true;
      return [`Serial.readStringUntil('\\n')`, Order.ATOMIC];
    };

    this.forBlock['esp32_uart2_begin'] = (block) => {
      const baud = block.getFieldValue('BAUD');
      const rx = block.getFieldValue('RX');
      const tx = block.getFieldValue('TX');
      P().usePin(Number(rx), 'uart2-rx', 'uart');
      P().usePin(Number(tx), 'uart2-tx', 'uart');
      return `Serial2.begin(${baud}, SERIAL_8N1, ${rx}, ${tx});\n`;
    };

    this.forBlock['esp32_uart2_print'] = (block) => {
      const value = this.val(block, 'VALUE', Order.NONE, this.cppString(''));
      const newline = block.getFieldValue('NEWLINE') === 'TRUE' || block.getFieldValue('NEWLINE') === true;
      return `Serial2.${newline ? 'println' : 'print'}(${value});\n`;
    };

    this.forBlock['esp32_uart2_available'] = () => ['(Serial2.available() > 0)', Order.ATOMIC];
    this.forBlock['esp32_uart2_read_byte'] = () => ['Serial2.read()', Order.ATOMIC];

    /* ---- Network ---- */

    this.forBlock['esp32_wifi_connect'] = (block) => {
      const ssid = this.cppString(block.getFieldValue('SSID') as string);
      const password = this.cppString(block.getFieldValue('PASSWORD') as string);
      P().usesWifi = true;
      P().include('WiFi.h');
      return `WiFi.mode(WIFI_STA);
WiFi.begin(${ssid}, ${password});
{
  uint32_t wifiStart = millis();
  while (WiFi.status() != WL_CONNECTED && millis() - wifiStart < 15000UL) {
    delay(200);
  }
}
if (WiFi.status() == WL_CONNECTED) {
  Serial.print(F("WiFi connected. IP: "));
  Serial.println(WiFi.localIP());
} else {
  Serial.println(F("WiFi connection failed"));
}
`;
    };

    this.forBlock['esp32_wifi_connected'] = () => {
      P().usesWifi = true;
      return ['(WiFi.status() == WL_CONNECTED)', Order.ATOMIC];
    };

    this.forBlock['esp32_wifi_local_ip'] = () => {
      P().usesWifi = true;
      return ['WiFi.localIP().toString()', Order.ATOMIC];
    };

    this.forBlock['esp32_wifi_disconnect'] = () => {
      P().usesWifi = true;
      return 'WiFi.disconnect(true);\nWiFi.mode(WIFI_OFF);\n';
    };

    this.forBlock['esp32_http_get'] = (block) => {
      const url = this.val(block, 'URL', Order.NONE, this.cppString('http://example.com/'));
      P().usesWifi = true;
      P().include('WiFi.h');
      P().include('HTTPClient.h');
      P().helper('httpget', HTTP_GET_HELPER);
      return [`httpGetBlock(${url})`, Order.ATOMIC];
    };

    this.forBlock['esp32_http_post_json'] = (block) => {
      const url = this.val(block, 'URL', Order.NONE, this.cppString('http://example.com/api'));
      const body = this.val(block, 'BODY', Order.NONE, this.cppString('{}'));
      P().usesWifi = true;
      P().include('WiFi.h');
      P().include('HTTPClient.h');
      P().helper('httppost', HTTP_POST_HELPER);
      return [`httpPostJsonBlock(${url}, ${body})`, Order.ATOMIC];
    };

    this.forBlock['esp32_mqtt_setup'] = (block) => {
      const host = this.cppString(block.getFieldValue('HOST') as string);
      const port = this.numField(block, 'PORT', 1883);
      const user = (block.getFieldValue('USER') as string) ?? '';
      const password = this.cppString((block.getFieldValue('PASSWORD') as string) ?? '');
      const clientId = this.cppString(block.getFieldValue('CLIENT_ID') as string);
      P().usesMqtt = true;
      P().include('WiFi.h');
      P().include('PubSubClient.h');
      P().library('PubSubClient');
      if (!P().globals.includes('WiFiClient espWifiClient;\nPubSubClient espMqtt(espWifiClient);')) {
        P().globals.push('WiFiClient espWifiClient;\nPubSubClient espMqtt(espWifiClient);');
      }
      P().setupLine('mqttserver', `espMqtt.setServer(${host}, ${port});`);
      if (user.length > 0) {
        P().setupLine(
          'mqttconnect',
          `if (!espMqtt.connect(${clientId}, ${this.cppString(user)}, ${password})) {\n  Serial.println(F("MQTT connection failed"));\n}`,
        );
      } else {
        P().setupLine(
          'mqttconnect',
          `if (!espMqtt.connect(${clientId})) {\n  Serial.println(F("MQTT connection failed"));\n}`,
        );
      }
      return '';
    };

    this.forBlock['esp32_mqtt_publish'] = (block) => {
      const topic = this.val(block, 'TOPIC', Order.NONE, this.cppString('topic'));
      const payload = this.val(block, 'PAYLOAD', Order.NONE, this.cppString(''));
      P().usesMqtt = true;
      return `espMqtt.publish(${topic}.c_str(), String(${payload}).c_str());\n`;
    };

    this.forBlock['esp32_mqtt_subscribe'] = (block) => {
      const topic = this.val(block, 'TOPIC', Order.NONE, this.cppString('topic'));
      P().usesMqtt = true;
      return `espMqtt.subscribe(${topic}.c_str());\n`;
    };

    this.forBlock['esp32_mqtt_loop'] = () => {
      P().usesMqtt = true;
      return 'espMqtt.loop();\n';
    };

    this.forBlock['esp32_mqtt_connected'] = () => {
      P().usesMqtt = true;
      return ['(espMqtt.connected())', Order.ATOMIC];
    };

    this.forBlock['esp32_mqtt_payload'] = () => {
      if (!P().inMqttHandler) {
        P().error('"MQTT payload" can only be used inside an "on MQTT message" block.');
      }
      return ['payload', Order.ATOMIC];
    };

    /* ---- Servo ---- */

    const servoEnsure = (pin: string): string => {
      const p = Number(pin);
      P().usesServo = true;
      P().usePin(p, 'servo', 'servo');
      if (!P().servoPins.has(pin)) {
        P().servoPins.add(pin);
        P().globals.push(`Servo servo_${pin};`);
        P().setupLine(
          'servotimers',
          'ESP32PWM::allocateTimer(0);\nESP32PWM::allocateTimer(1);\nESP32PWM::allocateTimer(2);\nESP32PWM::allocateTimer(3);',
        );
      }
      return `servo_${pin}`;
    };

    this.forBlock['esp32_servo_attach'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const obj = servoEnsure(pin);
      P().setupLine(`servoattach_${pin}`, `${obj}.attach(${pin}, 500, 2500);`, true);
      return '';
    };

    this.forBlock['esp32_servo_write'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const obj = servoEnsure(pin);
      const angle = this.val(block, 'ANGLE', Order.NONE, '90');
      P().setupLine(`servoattach_${pin}`, `${obj}.attach(${pin}, 500, 2500);`);
      return `${obj}.write(constrain(${angle}, 0, 180));\n`;
    };

    this.forBlock['esp32_servo_write_us'] = (block) => {
      const pin = block.getFieldValue('PIN');
      const obj = servoEnsure(pin);
      const us = this.val(block, 'US', Order.NONE, '1500');
      P().setupLine(`servoattach_${pin}`, `${obj}.attach(${pin}, 500, 2500);`);
      return `${obj}.writeMicroseconds(constrain(${us}, 500, 2500));\n`;
    };

    this.forBlock['esp32_servo_read'] = (block) => {
      const obj = servoEnsure(block.getFieldValue('PIN'));
      return [`${obj}.read()`, Order.ATOMIC];
    };

    this.forBlock['esp32_servo_detach'] = (block) => {
      const obj = servoEnsure(block.getFieldValue('PIN'));
      return `${obj}.detach();\n`;
    };

    /* ---- Motor drivers ---- */

    this.forBlock['esp32_motor_setup'] = (block) => {
      const model = block.getFieldValue('MODEL') as string;
      const in1 = Number(this.numField(block, 'IN1', 25));
      const in2 = Number(this.numField(block, 'IN2', 26));
      const in3 = Number(this.numField(block, 'IN3', 27));
      const in4 = Number(this.numField(block, 'IN4', 14));
      const ena = Number(this.numField(block, 'ENA', 32));
      const enb = Number(this.numField(block, 'ENB', 33));
      const stby = Number(this.numField(block, 'STBY', -1));
      if (P().motorConfigured) {
        P().warn('More than one "motor driver" setup block: only the first one is used.');
        return '';
      }
      P().motorConfigured = true;
      P().usesMotor = true;
      for (const [pin, role] of [
        [in1, 'motor-in1'], [in2, 'motor-in2'], [in3, 'motor-in3'], [in4, 'motor-in4'],
      ] as const) {
        P().usePin(Number(pin), role, 'motor');
      }
      const pwmOnEnable = model !== 'DRV8833';
      if (pwmOnEnable) {
        if (ena >= 0) P().usePin(Number(ena), 'motor-ena', 'motor');
        if (enb >= 0) P().usePin(Number(enb), 'motor-enb', 'motor');
        if (ena < 0) P().warn('Motor A has ENA = -1: motor A cannot be driven. Wire ENA or jumper it to 5V.');
        if (enb < 0) P().warn('Motor B has ENB = -1: motor B cannot be driven. Wire ENB or jumper it to 5V.');
      }
      if (stby >= 0) P().usePin(Number(stby), 'motor-stby', 'motor');

      P().globals.push(
        `// Motor driver ${model}\nconst int MTR_IN1 = ${in1}, MTR_IN2 = ${in2}, MTR_IN3 = ${in3}, MTR_IN4 = ${in4};` +
        (pwmOnEnable ? `\nconst int MTR_ENA = ${ena}, MTR_ENB = ${enb};` : '') +
        (stby >= 0 ? `\nconst int MTR_STBY = ${stby};` : ''),
      );
      P().functions.push(motorRunStopFunctions(model));
      const pinModes = [in1, in2, in3, in4, ...(pwmOnEnable && ena >= 0 ? [ena] : []), ...(pwmOnEnable && enb >= 0 ? [enb] : []), ...(stby >= 0 ? [stby] : [])]
        .map((p) => `pinMode(${p}, OUTPUT);`)
        .join('\n');
      P().setupLine(
        'motorpins',
        stby >= 0 ? `${pinModes}\ndigitalWrite(MTR_STBY, HIGH);` : pinModes,
      );
      return '';
    };

    this.forBlock['esp32_motor_run'] = (block) => {
      const motor = block.getFieldValue('MOTOR') as string;
      const dir = block.getFieldValue('DIR') as string;
      const speed = this.val(block, 'SPEED', Order.NONE, '200');
      const m = motor === 'A' ? '0' : motor === 'B' ? '1' : '2';
      P().usesMotor = true;
      return `espMotorRun(${m}, ${dir === 'BACKWARD' ? '-(' : ''}${speed}${dir === 'BACKWARD' ? ')' : ''});\n`;
    };

    this.forBlock['esp32_motor_stop'] = (block) => {
      const motor = block.getFieldValue('MOTOR') as string;
      const m = motor === 'A' ? '0' : motor === 'B' ? '1' : '2';
      P().usesMotor = true;
      return `espMotorStop(${m});\n`;
    };

    /* ---- Displays ---- */

    this.forBlock['esp32_oled_setup'] = (block) => {
      const size = (block.getFieldValue('SIZE') as string) ?? '128x64';
      const [w, h] = size.split('x');
      const addr = Number(block.getFieldValue('ADDR')) || 0x3c;
      if (P().displayKind !== 'none') {
        P().warn('More than one display setup block: only the first one is used.');
        return '';
      }
      P().displayKind = 'oled';
      P().usesDisplay = true;
      P().usesWire = true;
      P().include('Wire.h');
      P().include('Adafruit_SSD1306.h');
      P().library('Adafruit SSD1306');
      P().usesSerial = true;
      P().globals.push(`Adafruit_SSD1306 display(${w}, ${h}, &Wire, -1);`);
      P().setupLine(
        'displaybegin',
        `if (!display.begin(SSD1306_SWITCHCAPVCC, 0x${addr.toString(16)})) {\n  Serial.println(F("OLED not found"));\n}`,
      );
      P().setupLine('displayclear', 'display.clearDisplay();\ndisplay.display();');
      return '';
    };

    this.forBlock['esp32_tft_setup'] = (block) => {
      const driver = block.getFieldValue('DRIVER') as string;
      const cs = block.getFieldValue('CS');
      const dc = block.getFieldValue('DC');
      const rst = block.getFieldValue('RST');
      const rot = block.getFieldValue('ROT') ?? '0';
      if (P().displayKind !== 'none') {
        P().warn('More than one display setup block: only the first one is used.');
        return '';
      }
      P().displayKind = 'tft';
      P().usesDisplay = true;
      P().include('SPI.h');
      P().usePin(Number(cs), 'tft-cs', 'display');
      P().usePin(Number(dc), 'tft-dc', 'display');
      if (Number(rst) >= 0) P().usePin(Number(rst), 'tft-rst', 'display');
      const ilis = driver === 'ILI9341';
      P().include(ilis ? 'Adafruit_ILI9341.h' : 'Adafruit_ST7735.h');
      P().library(ilis ? 'Adafruit ILI9341' : 'Adafruit ST7735 and ST7789 Library');
      P().globals.push(ilis ? `Adafruit_ILI9341 display(${cs}, ${dc}, ${rst});` : `Adafruit_ST7735 display(${cs}, ${dc}, ${rst});`);
      const begin =
        driver === 'ILI9341'
          ? 'display.begin();'
          : driver === 'ST7735'
            ? 'display.initR(INITR_BLACKTAB);'
            : `display.init(${driver === 'ST7789_320' ? '240, 320' : '240, 240'});`;
      P().setupLine('displaybegin', `${begin}\ndisplay.setRotation(${rot});`);
      return '';
    };

    const displayReady = (): boolean => {
      P().usesDisplay = true;
      if (P().displayKind === 'none') return false;
      return true;
    };

    this.forBlock['esp32_display_fill'] = (block) => {
      if (!displayReady()) return '';
      const color = block.getFieldValue('COLOR');
      return `display.fillScreen(${color});\n`;
    };

    this.forBlock['esp32_display_clear'] = () => {
      if (!displayReady()) return '';
      return 'display.fillScreen(SPB_BLACK);\n';
    };

    this.forBlock['esp32_display_text'] = (block) => {
      if (!displayReady()) return '';
      const text = this.val(block, 'TEXT', Order.NONE, this.cppString(''));
      const x = this.val(block, 'X', Order.NONE, '0');
      const y = this.val(block, 'Y', Order.NONE, '0');
      const size = this.numField(block, 'SIZE', 2);
      const color = block.getFieldValue('COLOR');
      return `display.setTextSize(${size});\ndisplay.setTextColor(${color});\ndisplay.setCursor(${x}, ${y});\ndisplay.print(${text});\n`;
    };

    this.forBlock['esp32_display_pixel'] = (block) => {
      if (!displayReady()) return '';
      const x = this.val(block, 'X', Order.NONE, '0');
      const y = this.val(block, 'Y', Order.NONE, '0');
      return `display.drawPixel(${x}, ${y}, ${block.getFieldValue('COLOR')});\n`;
    };

    this.forBlock['esp32_display_line'] = (block) => {
      if (!displayReady()) return '';
      const x1 = this.val(block, 'X1', Order.NONE, '0');
      const y1 = this.val(block, 'Y1', Order.NONE, '0');
      const x2 = this.val(block, 'X2', Order.NONE, '0');
      const y2 = this.val(block, 'Y2', Order.NONE, '0');
      return `display.drawLine(${x1}, ${y1}, ${x2}, ${y2}, ${block.getFieldValue('COLOR')});\n`;
    };

    this.forBlock['esp32_display_rect'] = (block) => {
      if (!displayReady()) return '';
      const x = this.val(block, 'X', Order.NONE, '0');
      const y = this.val(block, 'Y', Order.NONE, '0');
      const w = this.val(block, 'W', Order.NONE, '10');
      const h = this.val(block, 'H', Order.NONE, '10');
      const color = block.getFieldValue('COLOR');
      const fill = block.getFieldValue('FILL') === 'FILL';
      return `display.${fill ? 'fillRect' : 'drawRect'}(${x}, ${y}, ${w}, ${h}, ${color});\n`;
    };

    this.forBlock['esp32_display_show'] = () => {
      if (!displayReady()) return '';
      if (P().displayKind === 'tft') {
        P().warn('"update display" is not needed on a TFT; it updates immediately.');
        return '';
      }
      return 'display.display();\n';
    };

    /* ---- Ethernet (LAN) ---- */

    const ethEnsure = (): string => {
      P().usesEth = true;
      P().usesSerial = true;
      if (!P().globals.includes('uint8_t spbEthMac[6] = {0xDE, 0xAD, 0xBE, 0xEF, 0xFE, 0xED};')) {
        P().globals.push('uint8_t spbEthMac[6] = {0xDE, 0xAD, 0xBE, 0xEF, 0xFE, 0xED};');
      }
      return 'spbEthMac';
    };

    const parseIpv4 = (text: string, label: string): string | null => {
      const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(text.trim());
      if (!m) {
        P().error(`"${text}" is not a valid IPv4 address for ${label}.`);
        return null;
      }
      return `IPAddress(${m.slice(1).join(', ')})`;
    };

    this.forBlock['esp32_eth_begin'] = (block) => {
      const cs = block.getFieldValue('CS');
      const mac = ethEnsure();
      P().usePin(Number(cs), 'eth-cs', 'eth');
      P().setupLine(
        'ethbegin',
        `Ethernet.init(${cs});\nif (Ethernet.begin(${mac}) == 0) {\n  Serial.println(F("Ethernet DHCP failed"));\n} else {\n  Serial.print(F("Ethernet IP: "));\n  Serial.println(Ethernet.localIP());\n}`,
        true,
      );
      return '';
    };

    this.forBlock['esp32_eth_begin_static'] = (block) => {
      const cs = block.getFieldValue('CS');
      const mac = ethEnsure();
      P().usePin(Number(cs), 'eth-cs', 'eth');
      const ip = parseIpv4(block.getFieldValue('IP') as string, 'IP');
      const gw = parseIpv4(block.getFieldValue('GATEWAY') as string, 'gateway');
      const sub = parseIpv4(block.getFieldValue('SUBNET') as string, 'subnet');
      if (!ip || !gw || !sub) return '';
      P().setupLine(
        'ethbegin',
        `Ethernet.init(${cs});\nEthernet.begin(${mac}, ${ip}, ${gw}, ${gw}, ${sub});`,
        true,
      );
      return '';
    };

    this.forBlock['esp32_eth_connected'] = () => {
      ethEnsure();
      return ['(Ethernet.linkStatus() == LinkON)', Order.ATOMIC];
    };

    this.forBlock['esp32_eth_local_ip'] = () => {
      ethEnsure();
      return ['Ethernet.localIP().toString()', Order.ATOMIC];
    };

    this.forBlock['esp32_eth_maintain'] = () => {
      ethEnsure();
      return 'Ethernet.maintain();\n';
    };

    /* ---- Control ---- */

    this.forBlock['controls_if'] = (block) => {
      // Derive the chain shape from the inputs actually present so generation
      // works identically for mutator-built blocks (browser) and serialized ones.
      let ifCount = 0;
      while (block.getInput(`IF${ifCount}`)) ifCount++;
      const elseCount = block.getInput('ELSE') ? 1 : 0;
      const elseifCount = ifCount - 1;

      if (elseifCount < 0) return '';

      let code = '';
      if (elseifCount === 0 && elseCount === 0) {
        const condition = this.val(block, 'IF0', Order.NONE, 'false');
        const body = this.stmtSeq(block, 'DO0');
        code = `if (${condition}) {\n${indent(body.trimEnd())}\n}\n`;
      } else {
        for (let i = 0; i <= elseifCount; i++) {
          const condition = this.val(block, `IF${i}`, Order.NONE, 'false');
          const body = this.stmtSeq(block, `DO${i}`);
          code += i === 0 ? `if (${condition}) {\n${indent(body.trimEnd())}\n}` : ` else if (${condition}) {\n${indent(body.trimEnd())}\n}`;
        }
        if (elseCount > 0) {
          const body = this.stmtSeq(block, 'ELSE');
          code += ` else {\n${indent(body.trimEnd())}\n}`;
        }
        code += '\n';
      }
      return code;
    };

    const emitIfBranch = (
      condition: string | null,
      body: string,
      keyword: string,
    ): string =>
      condition === null
        ? `${keyword} {\n${indent(body.trimEnd())}\n}`
        : `${keyword} (${condition}) {\n${indent(body.trimEnd())}\n}`;

    this.forBlock['esp32_if_else'] = (block) => {
      const condition = this.val(block, 'IF0', Order.NONE, 'false');
      const thenBody = this.stmtSeq(block, 'DO0');
      const elseBody = this.stmtSeq(block, 'ELSE');
      let code = emitIfBranch(condition, thenBody, 'if');
      code += ' ' + emitIfBranch(null, elseBody, 'else');
      return code + '\n';
    };

    this.forBlock['esp32_if_elseif_else'] = (block) => {
      const c0 = this.val(block, 'IF0', Order.NONE, 'false');
      const b0 = this.stmtSeq(block, 'DO0');
      const c1 = this.val(block, 'IF1', Order.NONE, 'false');
      const b1 = this.stmtSeq(block, 'DO1');
      const elseBody = this.stmtSeq(block, 'ELSE');
      let code = emitIfBranch(c0, b0, 'if');
      code += ' else ' + emitIfBranch(c1, b1, 'if');
      code += ' ' + emitIfBranch(null, elseBody, 'else');
      return code + '\n';
    };

    this.forBlock['controls_repeat_ext'] = (block) => {
      const times = this.val(block, 'TIMES', Order.NONE, '0');
      const id = sanitizeId(block.id);
      const body = this.stmtSeq(block, 'DO');
      return `for (int count_${id} = 0; count_${id} < ${times}; ++count_${id}) {\n${indent(body.trimEnd())}\n}\n`;
    };

    this.forBlock['controls_whileUntil'] = (block) => {
      const mode = block.getFieldValue('MODE');
      let condition = this.val(block, 'BOOL', Order.NONE, 'false');
      if (mode === 'UNTIL') condition = `!(${condition})`;
      const body = this.stmtSeq(block, 'DO');
      return `while (${condition}) {\n${indent(body.trimEnd())}\n}\n`;
    };

    this.forBlock['controls_for'] = (block) => {
      const info = this.varInfo(block, 'VAR');
      const name = info ? this.getVariableName(info.id) : 'i';
      const id = info?.id ?? `for:${block.id}`;
      const from = this.val(block, 'FROM', Order.NONE, '0');
      const to = this.val(block, 'TO', Order.NONE, '0');
      const by = this.val(block, 'BY', Order.NONE, '1');
      const up = !by.trim().startsWith('-');
      const op = up ? '<=' : '>=';
      this.plan.declaredVars.set(id, 'int');
      const body = this.stmtSeq(block, 'DO');
      return `for (int ${name} = ${from}; ${name} ${op} ${to}; ${name} += ${by}) {\n${indent(body.trimEnd())}\n}\n`;
    };

    this.forBlock['controls_flow_statements'] = (block) => {
      return block.getFieldValue('FLOW') === 'BREAK' ? 'break;\n' : 'continue;\n';
    };

    /* ---- Logic ---- */

    this.forBlock['logic_compare'] = (block) => {
      const ops: Record<string, [string, number]> = {
        EQ: ['==', Order.EQUALITY],
        NEQ: ['!=', Order.EQUALITY],
        LT: ['<', Order.RELATIONAL],
        LTE: ['<=', Order.RELATIONAL],
        GT: ['>', Order.RELATIONAL],
        GTE: ['>=', Order.RELATIONAL],
      };
      const [op, order] = ops[block.getFieldValue('OP')] ?? ['==', Order.EQUALITY];
      const a = this.val(block, 'A', order, '0');
      const b = this.val(block, 'B', order, '0');
      return [`${a} ${op} ${b}`, order];
    };

    this.forBlock['logic_operation'] = (block) => {
      const op = block.getFieldValue('OP') === 'OR' ? '||' : '&&';
      const order = op === '&&' ? Order.LOGICAL_AND : Order.LOGICAL_OR;
      const a = this.val(block, 'A', order, 'false');
      const b = this.val(block, 'B', order, 'false');
      return [`${a} ${op} ${b}`, order];
    };

    this.forBlock['logic_negate'] = (block) => {
      const condition = this.val(block, 'BOOL', Order.LOGICAL_NOT, 'false');
      return [`!${condition}`, Order.LOGICAL_NOT];
    };

    this.forBlock['logic_boolean'] = (block) => [
      block.getFieldValue('BOOL') === 'TRUE' ? 'true' : 'false',
      Order.ATOMIC,
    ];

    /* ---- Math ---- */

    this.forBlock['math_number'] = (block) => {
      const raw = block.getFieldValue('NUM');
      const n = Number(raw);
      return [Number.isFinite(n) ? String(n) : '0', Order.ATOMIC];
    };

    this.forBlock['math_arithmetic'] = (block) => {
      const op = block.getFieldValue('OP');
      if (op === 'POWER') {
        const a = this.val(block, 'A', Order.NONE, '0');
        const b = this.val(block, 'B', Order.NONE, '0');
        return [`pow(${a}, ${b})`, Order.ATOMIC];
      }
      const ops: Record<string, [string, number]> = {
        ADD: ['+', Order.ADDITIVE],
        MINUS: ['-', Order.ADDITIVE],
        MULTIPLY: ['*', Order.MULTIPLICATIVE],
        DIVIDE: ['/', Order.MULTIPLICATIVE],
      };
      const [symbol, order] = ops[op] ?? ['+', Order.ADDITIVE];
      const a = this.val(block, 'A', order, '0');
      const b = this.val(block, 'B', order, '0');
      return [`${a} ${symbol} ${b}`, order];
    };

    this.forBlock['math_modulo'] = (block) => {
      const a = this.val(block, 'DIVIDEND', Order.NONE, '0');
      const b = this.val(block, 'DIVISOR', Order.NONE, '1');
      return [`((long)(${a}) % (long)(${b}))`, Order.MULTIPLICATIVE];
    };

    this.forBlock['math_random_int'] = (block) => {
      const from = this.val(block, 'FROM', Order.NONE, '0');
      const to = this.val(block, 'TO', Order.NONE, '0');
      return [`random(${from}, (${to}) + 1)`, Order.ATOMIC];
    };

    this.forBlock['esp32_map'] = (block) => {
      const value = this.val(block, 'VALUE', Order.NONE, '0');
      const inMin = this.val(block, 'IN_MIN', Order.NONE, '0');
      const inMax = this.val(block, 'IN_MAX', Order.NONE, '1023');
      const outMin = this.val(block, 'OUT_MIN', Order.NONE, '0');
      const outMax = this.val(block, 'OUT_MAX', Order.NONE, '255');
      return [`map(${value}, ${inMin}, ${inMax}, ${outMin}, ${outMax})`, Order.ATOMIC];
    };

    this.forBlock['esp32_constrain'] = (block) => {
      const value = this.val(block, 'VALUE', Order.NONE, '0');
      const low = this.val(block, 'LOW', Order.NONE, '0');
      const high = this.val(block, 'HIGH', Order.NONE, '100');
      return [`constrain(${value}, ${low}, ${high})`, Order.ATOMIC];
    };

    /* ---- Variables & text ---- */

    this.forBlock['variables_get'] = (block) => {
      const info = this.varInfo(block, 'VAR');
      if (!info) return ['0', Order.ATOMIC];
      const { id } = info;
      const name = this.getVariableName(id);
      if (!P().declaredVars.has(id) && !P().warnedUndeclared.has(id)) {
        P().warnedUndeclared.add(id);
        P().warn(`Variable "${info.user}" is read before it is assigned.`);
      }
      return [name, Order.ATOMIC];
    };

    this.forBlock['variables_set'] = (block) => {
      const info = this.varInfo(block, 'VAR');
      if (!info) return '';
      const { id } = info;
      const name = this.getVariableName(id);
      const value = this.val(block, 'VALUE', Order.NONE, '0');
      if (!P().declaredVars.has(id)) {
        const type = inferCppType(value);
        P().declaredVars.set(id, type);
        return `${type} ${name} = ${value};\n`;
      }
      return `${name} = ${value};\n`;
    };

    this.forBlock['math_change'] = (block) => {
      const info = this.varInfo(block, 'VAR');
      if (!info) return '';
      const { id } = info;
      const name = this.getVariableName(id);
      const delta = this.val(block, 'DELTA', Order.NONE, '1');
      if (!P().declaredVars.has(id)) {
        P().declaredVars.set(id, 'int');
        return `int ${name} = 0;\n${name} += ${delta};\n`;
      }
      return `${name} += ${delta};\n`;
    };

    this.forBlock['esp32_variable_declare'] = (block) => {
      const type = block.getFieldValue('TYPE') as string;
      const name = (block.getFieldValue('NAME') as string).trim();
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
        P().error(`"${name || '(empty)'}" is not a valid variable name. Use letters, digits and underscores.`);
        return '';
      }
      if (P().declaredGlobals.has(name)) {
        P().warn(`Global "${name}" is declared more than once; keeping the first declaration.`);
        return '';
      }
      P().declaredGlobals.add(name);
      const initial = this.val(block, 'INITIAL', Order.NONE, '');
      const defaults: Record<string, string> = {
        int: '0',
        float: '0.0f',
        bool: 'false',
        String: '""',
        'volatile int': '0',
        'volatile bool': 'false',
      };
      const value = initial || defaults[type] || '0';
      P().globals.push(`${type} ${name} = ${value};`);
      return '';
    };

    this.forBlock['esp32_global_read'] = (block) => {
      const name = (block.getFieldValue('NAME') as string).trim();
      P().useGlobal(name);
      return [name, Order.ATOMIC];
    };

    this.forBlock['esp32_global_write'] = (block) => {
      const name = (block.getFieldValue('NAME') as string).trim();
      const value = this.val(block, 'VALUE', Order.NONE, '0');
      P().useGlobal(name);
      return `${name} = ${value};\n`;
    };

    this.forBlock['text'] = (block) => [
      this.cppString((block.getFieldValue('TEXT') as string) ?? ''),
      Order.ATOMIC,
    ];

    this.forBlock['esp32_text_join'] = (block) => {
      const a = this.val(block, 'A', Order.NONE, this.cppString(''));
      const b = this.val(block, 'B', Order.NONE, this.cppString(''));
      return [`String(${a}) + String(${b})`, Order.ADDITIVE];
    };
  }

  /* Declared late so handlers above can reference them by name. */
  private millisExpr(): string {
    return '(unsigned long)millis()';
  }
  private microsExpr(): string {
    return '(unsigned long)micros()';
  }
}

let generatorSingleton: CppGenerator | null = null;

export function getCppGenerator(): CppGenerator {
  if (!generatorSingleton) generatorSingleton = new CppGenerator();
  return generatorSingleton;
}

export function generateCpp(workspace: Blockly.Workspace): GenResult {
  return getCppGenerator().generate(workspace);
}
